import * as THREE from 'three/webgpu';

import { SketchDrawingTool } from '../../draw/DrawingSharedTypes';
import { ContourDragStrategy, PointDragStrategy, type AbstractEntityDragStrategy } from '../drag';
import type { EntitySelectionManager } from '../EntitySelectionManager';
import { isSketchContour } from '../../mesh';
import type { RawPointerEventPayload } from '../../viewer/moudles/EventManager';
import { SketchPointObject } from '../../mesh/sketch';
import { isFeatureLine } from '../../mesh/feature';
import { AbstractEntitySelectionHandler, DatumReferencePriorityHitKind } from './AbstractEntitySelectionHandler';
import { ImportedModelRaycastResolver } from '../../imported-model/selection/ImportedModelRaycastResolver';
import { ImportedModelTopologySelectionService } from '../../imported-model/selection/ImportedModelTopologySelectionService';

/**
 * 特征基础实体交互处理类。
 * 职责：
 * 1. 处理特征实体与草图基础对象的点击选择、hover、拖拽、框选；
 * 2. 补充普通基准命令需要的正式基准点 / 轴 / 面拾取；
 * 3. 该类名称保留历史 `Default`，但语义上表示“特征基础选择状态”，不是装配体空闲态的默认状态；
 * 4. 不处理裁剪模式与装配体模式专属行为。
 */
export class DefaultEntitySelectionHandler extends AbstractEntitySelectionHandler {
    /** 导入模型射线解析器。 */
    private readonly importedModelRaycastResolver: ImportedModelRaycastResolver;

    /** 导入模型拓扑选择服务。 */
    private readonly importedModelTopologySelectionService: ImportedModelTopologySelectionService;

    /**
     * 构造函数。
     * @param entitySelectionManager 实体选择管理器。
     */
    public constructor(entitySelectionManager: EntitySelectionManager) {
        super(entitySelectionManager);
        this.importedModelRaycastResolver = new ImportedModelRaycastResolver(entitySelectionManager.renderer);
        this.importedModelTopologySelectionService = new ImportedModelTopologySelectionService({
            renderer: entitySelectionManager.renderer,
            importedModelRaycastResolver: this.importedModelRaycastResolver,
        });
    }

    /** 释放本模式创建的拓扑服务及基类临时显示资源。 */
    public override dispose(): void {
        this.importedModelTopologySelectionService.dispose();
        super.dispose();
    }

    /**
     * 处理鼠标按下。
     * @param payload 原始事件。
     */
    public override onMouseDown(payload: RawPointerEventPayload): void {
        if (payload.button !== 0) {
            return;
        }
        if (this.entitySelectionManager.isSketchDrawingActive()) {
            return;
        }

        const picked = this.pickEntityAt(
            payload.clientX,
            payload.clientY,
            this.collectClickSelectionTargets(),
        );

        //------ 左键框选与 Ctrl+左键多选 开始------
        // 当 Ctrl+左键时，禁用点拖拽与轮廓拖拽，允许点击增加多选继续处理。
        if (payload.ctrlKey) {
            this.entitySelectionManager.setMouseDownStart(new THREE.Vector2(payload.clientX, payload.clientY));
            this.entitySelectionManager.setIsBoxDragging(false);
            this.entitySelectionManager.setBoxDragEnd(null);
            this.resetSelectionBoxVisual();
            this.entitySelectionManager.setSuppressNextClick(false);
            return;
        }
        //------ 左键框选与 Ctrl+左键多选 结束------

        if (picked?.object instanceof SketchPointObject) {
            if (
                this.entitySelectionManager.renderer.sketchDrawingManager?.isDrawingActive()
                && (this.entitySelectionManager.renderer.sketchDrawingManager?.getCurrentTool() || null) === SketchDrawingTool.Point
            ) {
                return;
            }

            const pointObject = picked.object;
            this.entitySelectionManager.setMouseDownStart(null);
            this.entitySelectionManager.setIsBoxDragging(false);
            this.entitySelectionManager.setBoxDragEnd(null);
            this.resetSelectionBoxVisual();
            this.entitySelectionManager.setSuppressNextClick(false);

            if (this.entitySelectionManager.getCurrentSelectedPoint() !== pointObject) {
                this.entitySelectionManager.updateSelectionState(
                    this.resolveNextSelectionState(this.toIntersections([pointObject]), false),
                );
            }

            if (!pointObject.canDrag()) {
                this.entitySelectionManager.setSuppressNextClick(true);
                payload.nativeEvent.preventDefault();
                return;
            }

            if (!this.isPointDragEnabled()) {
                this.entitySelectionManager.setSuppressNextClick(true);
                payload.nativeEvent.preventDefault();
                return;
            }

            this.startDrag(new PointDragStrategy(this.entitySelectionManager.renderer, pointObject), payload);
            this.entitySelectionManager.setSuppressNextClick(true);
            payload.nativeEvent.preventDefault();
            return;
        }

        if (picked && isSketchContour(picked.object)) {
            const contour = picked.object;
            this.entitySelectionManager.setMouseDownStart(null);
            this.entitySelectionManager.setIsBoxDragging(false);
            this.entitySelectionManager.setBoxDragEnd(null);
            this.resetSelectionBoxVisual();
            this.entitySelectionManager.setSuppressNextClick(false);

            // 特征基础选择状态下，轮廓只允许拖拽，不再映射为槽口/关联线选择，避免触发轮廓联动高亮。

            if (this.isContourDragEnabled()) {
                this.startDrag(new ContourDragStrategy(this.entitySelectionManager.renderer, contour), payload);
            }
            this.entitySelectionManager.setSuppressNextClick(true);
            payload.nativeEvent.preventDefault();
            return;
        }

        this.entitySelectionManager.setMouseDownStart(new THREE.Vector2(payload.clientX, payload.clientY));
        this.entitySelectionManager.setIsBoxDragging(false);
        this.entitySelectionManager.setBoxDragEnd(null);
        this.resetSelectionBoxVisual();
        this.entitySelectionManager.setSuppressNextClick(false);
    }

    /**
     * 处理鼠标移动。
     * @param payload 原始事件。
     */
    public override onMouseMove(payload: RawPointerEventPayload): void {
        const activeDragStrategy = this.entitySelectionManager.getActiveDragStrategy();
        if (activeDragStrategy) {
            activeDragStrategy.update(payload);
            return;
        }

        //------ 左键框选与 Ctrl+左键多选 开始------
        // 左键拖动即触发框选；Ctrl 仅影响点击时的多选语义，不再作为框选前置条件。
        const mouseDownStart = this.entitySelectionManager.getMouseDownStart();
        if (this.isBoxSelectionEnabled() && mouseDownStart && payload.buttons !== 0) {
            const dx = payload.clientX - mouseDownStart.x;
            const dy = payload.clientY - mouseDownStart.y;
            const distance = Math.sqrt(dx * dx + dy * dy);
            if (distance > this.entitySelectionManager.getBoxDragThreshold() || this.entitySelectionManager.getIsBoxDragging()) {
                this.entitySelectionManager.setIsBoxDragging(true);
                this.entitySelectionManager.setBoxDragEnd(new THREE.Vector2(payload.clientX, payload.clientY));
                this.clearHoverState();
                this.updateSelectionBoxVisual(mouseDownStart, new THREE.Vector2(payload.clientX, payload.clientY));
                return;
            }
        }

        //------ 装配体模式禁用框选 开始------
        // 当模式禁用框选时，拖拽超过阈值后只抑制 click，避免鼠标释放时把一次拖拽误结算为点击选择。
        if (!this.isBoxSelectionEnabled() && mouseDownStart && payload.buttons !== 0) {
            const dx = payload.clientX - mouseDownStart.x;
            const dy = payload.clientY - mouseDownStart.y;
            const distance = Math.sqrt(dx * dx + dy * dy);
            if (distance > this.entitySelectionManager.getBoxDragThreshold()) {
                this.entitySelectionManager.setSuppressNextClick(true);
            }
        }
        //------ 装配体模式禁用框选 结束------

        // 当所有鼠标按钮都释放时，清空 mouseDownStart，防止框选基于过期起点被意外触发
        if (payload.buttons === 0 && this.entitySelectionManager.getMouseDownStart()) {
            this.entitySelectionManager.setMouseDownStart(null);
            this.entitySelectionManager.setIsBoxDragging(false);
            this.entitySelectionManager.setBoxDragEnd(null);
            this.resetSelectionBoxVisual();
        }
        //------ 左键框选与 Ctrl+左键多选 结束------

        // OrbitControls 旋转/平移/缩放视角时同样会持续触发 mousemove。
        // 此时继续执行全量 hover picking 会把相机交互拖慢，尤其在插入后的导入模型场景里更明显。
        // 这里直接跳过 hover 拾取，并清掉残留 hover，高亮将在鼠标松开后下一次普通移动时重新建立。
        if (payload.buttons !== 0) {
            this.clearHoverState();
            return;
        }

        this.queueHoverInput(payload.clientX, payload.clientY);
    }

    /**
     * 处理鼠标抬起。
     * @param payload 原始事件。
     */
    public override onMouseUp(payload: RawPointerEventPayload): void {
        if (payload.button !== 0) {
            return;
        }

        if (this.entitySelectionManager.getActiveDragStrategy()) {
            this.stopDrag();
            this.entitySelectionManager.setMouseDownStart(null);
            this.entitySelectionManager.setIsBoxDragging(false);
            this.entitySelectionManager.setBoxDragEnd(null);
            this.resetSelectionBoxVisual();
            this.entitySelectionManager.setSuppressNextClick(true);
            return;
        }

        const dragStart = this.entitySelectionManager.getMouseDownStart();
        if (!dragStart) {
            return;
        }

        const isBoxDragging = this.entitySelectionManager.getIsBoxDragging();
        this.entitySelectionManager.setMouseDownStart(null);
        this.entitySelectionManager.setIsBoxDragging(false);
        this.entitySelectionManager.setBoxDragEnd(null);
        this.resetSelectionBoxVisual();

        if (isBoxDragging && this.isBoxSelectionEnabled()) {
            // 左键拖动统一执行框选；Ctrl 只影响点击多选，不再改变框选的结算语义。
            this.performBoxSelection(dragStart, new THREE.Vector2(payload.clientX, payload.clientY), false);
            this.entitySelectionManager.setSuppressNextClick(true);
        }
    }

    /**
     * 处理鼠标点击。
     * @param payload 原始事件。
     */
    public override onClick(payload: RawPointerEventPayload): void {
        if (payload.button !== 0) {
            return;
        }
        if (this.entitySelectionManager.isSketchDrawingActive()) {
            return;
        }
        if (this.entitySelectionManager.getSuppressNextClick()) {
            this.entitySelectionManager.setSuppressNextClick(false);
            return;
        }

        const rawPicked = this.pickEntityAt(
            payload.clientX,
            payload.clientY,
            this.collectClickSelectionTargets(),
        );
        const picked = this.resolveClickSelectionPick(rawPicked);

        if (
            picked?.object instanceof SketchPointObject
            && (this.entitySelectionManager.renderer.sketchDrawingManager?.isDrawingActive() || false)
            && (this.entitySelectionManager.renderer.sketchDrawingManager?.getCurrentTool() || null) === SketchDrawingTool.Point
        ) {
            return;
        }

        if (picked && isSketchContour(picked.object)) {
            // 特征基础选择状态点击轮廓不改变选择集，防止触发关联边选中高亮。
            return;
        }

        if (picked) {
            this.entitySelectionManager.updateSelectionState(this.resolveNextSelectionState([picked], payload.ctrlKey));
            return;
        }

        if (payload.ctrlKey) {
            return;
        }

        this.entitySelectionManager.updateSelectionState([]);
    }

    //------ 正式基准对象拾取补齐 开始------
    /**
     * 收集点击候选对象。
     *
     * 说明：
     * 1. 抽象基类实现只覆盖草图对象与特征对象；
     * 2. 这里补充正式基准面/轴/点，使普通基准命令及特征基础选择链路能拾取这些对象；
     * 3. 基准面以 mesh 为唯一语义对象，边框命中后会在 normalize 阶段回溯到 mesh。
     *
     * @returns 候选对象列表。
     */
    protected override collectClickSelectionTargets(): THREE.Object3D[] {
        return this.collectDefaultAndDatumInteractionTargets();
    }

    /**
     * 收集测量叠加层点击候选对象。
     *
     * @returns 候选对象列表。
     */
    public collectMeasureClickSelectionTargets(): THREE.Object3D[] {
        return this.collectDefaultAndDatumInteractionTargets();
    }

    /**
     * 收集移入候选对象。
     *
     * @returns 候选对象列表。
     */
    protected override collectHoverSelectionTargets(): THREE.Object3D[] {
        return this.collectDefaultAndDatumInteractionTargets();
    }

    /**
     * 收集测量叠加层移入候选对象。
     *
     * @returns 候选对象列表。
     */
    public collectMeasureHoverSelectionTargets(): THREE.Object3D[] {
        return this.collectDefaultAndDatumInteractionTargets();
    }

    /**
     * 判断当前默认选择状态是否需要接入导入模型拓扑拾取。
     *
     * @returns 需要按导入拓扑代理拾取时返回 `true`。
     */
    protected shouldEnableImportedTopologySelection(): boolean {
        const selectionMode = this.entitySelectionManager.getSelectionMode();
        return selectionMode === 'object'
            || selectionMode === 'edge'
            || selectionMode === 'face'
            || selectionMode === 'vertex'
            || selectionMode === 'face-edge'
            || selectionMode === 'topology';
    }

    /**
     * 合并默认候选与导入模型可见 GLB 候选。
     *
     * @param baseTargets 默认候选。
     * @returns 去重后的候选集合。
     */
    protected mergeImportedTopologySelectionTargets(baseTargets: THREE.Object3D[]): THREE.Object3D[] {
        if (!this.shouldEnableImportedTopologySelection()) {
            return baseTargets;
        }

        const targetMap = new Map<string, THREE.Object3D>();
        baseTargets.forEach((target) => {
            targetMap.set(target.uuid, target);
        });
        this.importedModelTopologySelectionService.collectRaycastTargets().forEach((target) => {
            if (!targetMap.has(target.uuid)) {
                targetMap.set(target.uuid, target);
            }
        });
        return Array.from(targetMap.values());
    }

    /**
     * 在默认选择状态内解析导入模型拓扑命中。
     *
     * @param clientX 屏幕 X 坐标。
     * @param clientY 屏幕 Y 坐标。
     * @param interactableObjects 当前候选对象。
     * @returns imported-model 拓扑命中；未命中时返回 `null`。
     */
    private pickImportedTopologyAt(
        clientX: number,
        clientY: number,
        interactableObjects: THREE.Object3D[],
    ): THREE.Intersection<THREE.Object3D> | null {
        if (!this.shouldEnableImportedTopologySelection()) {
            return null;
        }

        return this.importedModelTopologySelectionService.pickAt({
            clientX,
            clientY,
            interactableObjects,
            selectionMode: this.entitySelectionManager.getSelectionMode(),
            allowOccludedSelection: this.entitySelectionManager.getAllowOccludedFeatureTopologySelection(),
        });
    }

    /**
     * 从默认命中与导入拓扑命中中选择最终命中。
     *
     * @param defaultPick 默认命中。
     * @param importedPick 导入拓扑命中。
     * @returns 最终命中。
     */
    private resolveDefaultModePick(
        defaultPick: THREE.Intersection<THREE.Object3D> | null,
        importedPick: THREE.Intersection<THREE.Object3D> | null,
    ): THREE.Intersection<THREE.Object3D> | null {
        if (!defaultPick) {
            return importedPick;
        }
        if (!importedPick) {
            return defaultPick;
        }
        return defaultPick.distance <= importedPick.distance ? defaultPick : importedPick;
    }

    /**
     * 根据屏幕坐标拾取实体。
     *
     * 说明：
     * 1. 草图与特征实体统一委托给 `EntitySelectionQueryHelpers`，避免重复实现隐藏对象过滤；
     * 2. 基准点/轴/面是特征基础选择链路的额外扩展语义，在统一实体拾取后独立补充；
     * 3. 基准点/轴与特征点/线重合时继续保留原有优先级，保证 reference 对象可直接被选中。
     *
     * @param clientX 屏幕 X 坐标。
     * @param clientY 屏幕 Y 坐标。
     * @param interactableObjects 可参与拾取的候选对象列表。
     * @returns 命中实体；未命中返回 `null`。
     */
    protected override pickEntityAt(
        clientX: number,
        clientY: number,
        interactableObjects: THREE.Object3D[],
    ): THREE.Intersection<THREE.Object3D> | null {
        const entityPick = super.pickEntityAt(clientX, clientY, interactableObjects);
        const importedPick = this.pickImportedTopologyAt(clientX, clientY, interactableObjects);
        const datumPick = this.pickDatumEntityAt(clientX, clientY, interactableObjects);
        if (
            datumPick
            && this.resolveDatumPointOrAxisHitKind(datumPick.object) !== DatumReferencePriorityHitKind.None
        ) {
            return datumPick;
        }
        return this.resolveDefaultModePick(entityPick ?? datumPick, importedPick);
    }

    /**
     * 拾取特征基础选择链路下的正式基准对象。
     *
     * @param clientX 屏幕 X 坐标。
     * @param clientY 屏幕 Y 坐标。
     * @param interactableObjects 当前候选对象。
     * @returns 基准命中；未命中时返回 null。
     */
    private pickDatumEntityAt(
        clientX: number,
        clientY: number,
        interactableObjects: THREE.Object3D[],
    ): THREE.Intersection<THREE.Object3D> | null {
        return this.pickDatumReferenceAt(clientX, clientY, interactableObjects);
    }

    /**
     * 统一收集特征基础对象与正式基准对象的交互候选。
     *
     * @returns 候选对象列表。
     */
    private collectDefaultAndDatumInteractionTargets(): THREE.Object3D[] {
        return this.mergeImportedTopologySelectionTargets(this.collectDatumReferenceInteractionTargets(
            super.collectClickSelectionTargets(),
        ));
    }
    //------ 正式基准对象拾取补齐 结束------

    /**
     * 启动拖拽策略。
     * @param strategy 拖拽策略实例。
     * @param payload 拖拽启动事件载荷。
     */
    protected startDrag(strategy: AbstractEntityDragStrategy, payload: RawPointerEventPayload): void {
        this.entitySelectionManager.renderer.interactionStateManager?.setRelationMarkerHiddenDuringDrag(true);
        try {
            strategy.start(payload);
            this.entitySelectionManager.setActiveDragStrategy(strategy);
        } catch (error) {
            this.entitySelectionManager.renderer.interactionStateManager?.setRelationMarkerHiddenDuringDrag(false);
            throw error;
        }
    }

    /**
     * 停止当前拖拽策略。
     */
    private stopDrag(): void {
        const activeDragStrategy = this.entitySelectionManager.getActiveDragStrategy();
        if (!activeDragStrategy) {
            return;
        }
        try {
            const affectedSketchId = activeDragStrategy.resolveAffectedSketchId();
            const hasGeometryChanged = activeDragStrategy.hasGeometryChanged();
            activeDragStrategy.stop();
            if (hasGeometryChanged && affectedSketchId) {
                this.entitySelectionManager.renderer.sketchGroupManager?.getGroupById(affectedSketchId)?.commitDefinitionChanges({
                    reason: 'drag-finished',
                });
            }
            this.entitySelectionManager.setActiveDragStrategy(null);
        } finally {
            this.entitySelectionManager.renderer.interactionStateManager?.setRelationMarkerHiddenDuringDrag(false);
        }
    }

    /**
     * 特征基础选择状态禁用框选事件派发。
     * 说明：
     * 1. 特征基础选择状态框选只负责更新 SelectionState；
     * 2. 不发布 EntityBoxSelected / MultipleEntitiesBoxSelected，避免触发上层面板联动。
     * @param selectedObjects 本次框选命中对象。
     * @param isCrossing 是否 crossing 框选。
     * @param selectionRect 框选屏幕矩形。
     */
    protected override publishBoxSelectionEvents(
        selectedObjects: THREE.Object3D[],
        isCrossing: boolean,
        selectionRect: { minX: number; minY: number; maxX: number; maxY: number },
    ): void {
        void selectedObjects;
        void isCrossing;
        void selectionRect;
    }

    /**
     * 收集框选候选对象。
     * @returns 候选对象列表。
     */
    protected override collectBoxSelectionTargets(): THREE.Object3D[] {
        return super.collectBoxSelectionTargets().filter(isFeatureLine);
    }
}
